import type { CurrentAdmin } from '@/composables/useCurrentAdmin'

// shouldBounceSetupToLogin decides whether a navigation into /setup/* must
// be redirected to /auth/login. The /setup/* gate exists so the wizard never
// paints its chrome without a session (loadServerStep swallows 401s), but it
// used to bounce on ANY falsy result from fetchCurrentAdmin() — which reads
// the same for a genuinely revoked session and for one transient /me or
// refresh blip.
//
// That difference is not cosmetic. It is the 2026-06-14 incident: three
// forced logouts in 27 minutes on prod with healthy DB session state, root-
// caused to a redirect that fired on `refreshed === false` without checking
// whether the refresh had actually been REJECTED. apiClient was hardened for
// it (see the `outcome.rejected` gate in utils/apiClient.ts); this guard was
// not, and it is the first blocking /me round-trip a freshly onboarded
// operator hits on the way out of the wizard — after a WebAuthn ceremony,
// recovery codes, and a legal document, i.e. with real elapsed time against
// a ~15-minute access token.
//
// The signal is the identity CACHE, and the contract that makes it reliable
// is stated in useCurrentAdmin: a failed fetch does NOT clear `current`.
// Only `invalidateCurrentAdmin()` does, and apiClient calls it on exactly
// one branch — a refresh the server explicitly rejected. So:
//
//   fetched null + cached present → the session was never rejected; a
//                                   request failed. Let the operator through.
//   fetched null + cached absent  → nothing says a session exists. Bounce.
//
// Presence only, never identity: a cached row for a different admin still
// means a session exists, and deciding whose it is belongs to the server —
// AdminAuth's workspace guard rejects a mismatch. Guessing here would be a
// second, weaker copy of a check the backend already owns.
//
// Pure so the router guard stays a one-liner and the truth table is unit-
// tested rather than reasoned about inside a beforeEach.
export function shouldBounceSetupToLogin(
  fetched: CurrentAdmin | null | undefined,
  cached: CurrentAdmin | null | undefined,
): boolean {
  return !fetched && !cached
}
